import { lazy, Suspense } from "react";
import "@/i18n";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AppProviders, useAuth } from "@/context/AppContext";
import { isStaff } from "@/lib/utils";
import Home from "@/pages/Home";
import { Login, Register } from "@/pages/Auth";
import RequestService from "@/pages/RequestService";
import { Services, ServiceDetail, SeoLanding } from "@/pages/Services";
import { Search, Nearby, PartnerProfile } from "@/pages/Discover";
import { PartnerDashboard, PartnerProfileEdit, CustomerDashboard } from "@/pages/Dashboards";
import { Orders, OrderDetails } from "@/pages/Orders";
import { Wallet, TopUp } from "@/pages/Wallet";
import { Notifications, Messages, Reviews, Complaints, Account, Legal, StaticPage, BecomePartner } from "@/pages/Misc";

const Admin = lazy(() => import("@/pages/Admin"));

const Guard = ({ roles, children }) => {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <div className="grid min-h-screen place-items-center"><div className="h-10 w-10 animate-spin rounded-full border-4 border-[#06B6D4] border-t-transparent" /></div>;
  if (!user) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  if (roles && !roles.includes(user.role) && !(roles.includes("admin") && isStaff(user))) return <Navigate to={isStaff(user) ? "/admin" : user.role === "partner" ? "/partner" : "/dashboard"} replace />;
  return children;
};

function PartnerGate() {
  const { user } = useAuth();
  if (user === null) return null;
  if (user && user.role === "partner") return <BecomePartner />;
  return <Navigate to="/register" replace state={{ role: "partner" }} />;
}

function App() {
  return (
    <AppProviders>
      <BrowserRouter>
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/:slug" element={<ServiceDetail />} />
            <Route path="/s/:service/:gov" element={<SeoLanding />} />
            <Route path="/request" element={<RequestService />} />
            <Route path="/search" element={<Search />} />
            <Route path="/nearby" element={<Nearby />} />
            <Route path="/partners/:id" element={<PartnerProfile />} />
            <Route path="/become-partner" element={<PartnerGate />} />
            <Route path="/how-it-works" element={<StaticPage kind="how" />} />
            <Route path="/about" element={<StaticPage kind="about" />} />
            <Route path="/contact" element={<StaticPage kind="contact" />} />
            <Route path="/faq" element={<StaticPage kind="faq" />} />
            <Route path="/legal/:slug" element={<Legal />} />
            <Route path="/dashboard" element={<Guard roles={["customer"]}><CustomerDashboard /></Guard>} />
            <Route path="/partner" element={<Guard roles={["partner"]}><PartnerDashboard /></Guard>} />
            <Route path="/partner/profile" element={<Guard roles={["partner"]}><PartnerProfileEdit /></Guard>} />
            <Route path="/orders" element={<Guard><Orders /></Guard>} />
            <Route path="/orders/:id" element={<Guard><OrderDetails /></Guard>} />
            <Route path="/wallet" element={<Guard roles={["partner"]}><Wallet /></Guard>} />
            <Route path="/wallet/topup" element={<Guard roles={["partner"]}><TopUp /></Guard>} />
            <Route path="/notifications" element={<Guard><Notifications /></Guard>} />
            <Route path="/messages" element={<Guard><Messages /></Guard>} />
            <Route path="/reviews" element={<Guard><Reviews /></Guard>} />
            <Route path="/complaints" element={<Guard><Complaints /></Guard>} />
            <Route path="/account" element={<Guard><Account /></Guard>} />
            <Route path="/admin/*" element={<Guard roles={["admin"]}><Admin /></Guard>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </AppProviders>
  );
}

export default App;
